import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";
import LoadingButton from "@/components/common/buttons/loadingButton";
import { FormPhoneField } from "@/components/serviceFlow/clientDetails/forms/formFields";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useGenerateBooking2FaForClientMutation } from "@/graphql/mutations/generateBooking2FAForClient.graphql.types";
import useAction from "@/hooks/misc/useAction";
import { BookingStep } from "@/types/bookingTypes";
import { BookingReturningClientErrors } from "@/utils/booking";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import useErrorLogger from "@/utils/useErrorLogger";
import BookingInfoCard from "../bookingInfoCard";

type PhoneInput = {
  phone: string;
};

const schema = yup.object().shape({
  phone: yup
    .string()
    .test((value: string) => {
      return isValidPhoneNumber(value);
    })
    .required(),
});

export default function BookingPhoneNumber() {
  const { setStep, setClientDetails, medspaData } = useClientBookingContext();
  const logError = useErrorLogger();

  const { control, setValue, watch } = useForm<PhoneInput>({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const [generateBooking2FAForClient] =
    useGenerateBooking2FaForClientMutation();

  const phone = watch("phone");

  const onNext = async () => {
    try {
      await generateBooking2FAForClient({
        variables: {
          clientPhoneNumber: phone,
          medspaSlug: medspaData.slug,
        },
      });

      setClientDetails((prev) => {
        return { ...prev, phone };
      });

      setStep(BookingStep.CODE_INPUT);
    } catch (e) {
      if (e.message === BookingReturningClientErrors.CLIENT_NOT_FOUND) {
        setClientDetails((prev) => ({ ...prev, phone }));
        setStep(BookingStep.CLIENT_DETAILS);
        return;
      }

      if (e.message === BookingReturningClientErrors.MULTIPLE_CLIENTS_EXITS) {
        setStep(BookingStep.PHONE_CONFLICT);
        return;
      }

      if (e.message === BookingReturningClientErrors.TOO_MANY_ATTEMPTS) {
        toast.error("Too many attempts. Please try again in 30 seconds.");
        return;
      }

      if (e.message === BookingReturningClientErrors.INACTIVE_CLIENT) {
        toast.error(
          "We are sorry you are unable to book at this time. Please contact the provider for more information."
        );
        return;
      }

      logError(e);
      toast.error(
        hasDjangoMutationError(e)
          ? e.message
          : "Unable to verify phone number. Please contact support team."
      );
    }
  };

  const { handleAction, isLoading } = useAction(onNext);

  return (
    <BookingInfoCard>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          pb: 3,
        }}
      >
        Provide your phone number
      </Typography>
      <Stack spacing={2}>
        <Typography>
          Enter your phone number, so we can guide you through the booking
          process!
        </Typography>
        <FormPhoneField<PhoneInput>
          control={control}
          name="phone"
          setValue={setValue}
        />
        <LoadingButton
          variant="contained"
          disabled={!isValidPhoneNumber(phone || "")}
          onClick={handleAction}
          loading={isLoading}
        >
          Next
        </LoadingButton>
      </Stack>
    </BookingInfoCard>
  );
}
